import { globalRemoveMaskReal2, globalcleanDocument, globalcleanPhone } from "../../../../shared/utils";
import { ClientCompanyData, FireCreateQuotePayload } from "../components/types/fire";

interface QuotationDependencies {
    clientId: string | null;
    companyId: string | null;
    brokerageId?: string | number;
    selectionData: any;
    localCoverages: {
        id?: string | number;
        coverageId?: string | number;
        value?: string | number;
        coverage_value?: string | number;
        coverage_prizeTotal?: string | number;
        limite_min?: string | number;
        limite_max?: string | number;
        integrator_code?: string;
    }[];
    companyData?: ClientCompanyData;
    totalQuotation: number;
    transformDateToISO: (dateString: string | undefined) => string | null;
    quotationResultCoverages?: QuotationDependencies["localCoverages"];
}

// 🧩 Nova função de conversão inteligente
const normalizeNumber = (v: any): number => {
    if (v === null || v === undefined || v === "") return 0;

    // Se já for número puro, retorna diretamente
    if (typeof v === "number") return v;

    // Se for string numérica simples (sem vírgulas/pontos)
    if (/^\d+(\.\d+)?$/.test(v)) return Number(v);

    // Caso contrário, aplica a limpeza da máscara (usa função global)
    return Number(globalRemoveMaskReal2(String(v)));
};

export const mapFormDataToQuotationSavePayload = (
    formData: any,
    { clientId, companyId, brokerageId: brokerageIdParam, selectionData, localCoverages, companyData, totalQuotation, transformDateToISO, quotationResultCoverages }: QuotationDependencies
): FireCreateQuotePayload => {
    // 🗓️ Datas
    const validity_start = transformDateToISO(formData.vigencyStart) ?? new Date().toISOString();
    const monthly_validity = Number(formData.vigencyMonths ?? formData.installments ?? 12);

    // 📊 Percentuais
    const pc_predial =
        normalizeNumber(selectionData?.predialPercent) || normalizeNumber(formData?.PredialPercent) || normalizeNumber(companyData?.pc_predio ?? "80,00");

    const pc_content =
        normalizeNumber(selectionData?.contentPercent) || normalizeNumber(formData?.ContentPercent) || normalizeNumber(companyData?.pc_conteudo ?? "20,00");

    // 💰 Valores principais
    const rent_value = normalizeNumber(selectionData?.rentValue ?? formData?.rent_value);
    const insurance_prize = totalQuotation;
    const brokerage_comission_percent_amount = normalizeNumber(companyData?.comission_broker);
    const prolabore_percent_amount = normalizeNumber(formData?.prolabore_percent_amount ?? selectionData?.prolabore);
    const installment_number = Number(formData?.installments ?? selectionData?.installments ?? 1);

    // 👤 Inquilino (tenant)
    const tenant_name = formData?.tenantName ?? "";
    const tenant_document = globalcleanDocument(formData?.tenantDocument);
    const tenant_birth_date = transformDateToISO(formData?.tenantBirthDate) ?? "";
    const tenant_email = formData?.tenantEmail ?? "";
    const tenant_mobile_phone = globalcleanPhone(formData?.tenantMobilePhone);
    const tenant_social_name = formData?.tenantSocialName ?? "";

    // 👤 Locador (lessor)
    const lessor_name = formData?.lessorName ?? "";
    const lessor_document = globalcleanDocument(formData?.lessorDocument);
    const lessor_email = formData?.lessorEmail ?? "";

    // 🏠 Endereço
    const address_zipcode = formData?.address_zipcode ?? "";
    const address_street = formData?.address_street ?? "";
    const address_residence_type = formData?.address_residence_type ?? "";
    const address_number = formData?.address_number ?? "";
    const address_complement = formData?.address_complement ?? "";
    const address_district = formData?.address_district ?? "";
    const address_city = formData?.address_city ?? "";
    const address_state = formData?.address_state ?? "";
    const address_ibge = formData?.address_ibge ?? "";

    // 📋 Relações
    const assistanceId = formData?.assistanceId ?? selectionData?.assistanceId ?? "";
    const occupationId = formData?.idOcupacao ?? selectionData?.occupationId ?? "";

    // Prioridade: Param > CompanyData > FormData > 0
    const brokerageId = Number(brokerageIdParam ?? companyData?.brokerage_id ?? companyData?.brokerage_code ?? formData?.brokerage_code ?? 0);

    // 💡 Origem das coberturas
    const baseCoverages = quotationResultCoverages?.length ? quotationResultCoverages : localCoverages;

    // 🔹 Mapeia coberturas normalizadas
    const coverages = baseCoverages.map((c) => ({
        coverageId: String(c.coverageId ?? c.id ?? ""),
        coverage_value: normalizeNumber(c.coverage_value ?? c.value ?? 0),
        coverage_prizeTotal: normalizeNumber(c.coverage_prizeTotal ?? 0),
        limite_min: normalizeNumber(c.limite_min ?? 0),
        limite_max: normalizeNumber(c.limite_max ?? 0),
        integrator_code: String(c.integrator_code ?? ""),
    }));

    // 🧾 Montagem final do payload
    return {
        id: formData?.id ?? undefined,
        clientId: formData?.clientId ?? clientId ?? "",
        insurance_companiesId: formData?.insurance_companiesId ?? companyId ?? "",
        assistanceId,
        occupationId,
        brokerageId,

        tenant_name,
        tenant_document,
        tenant_birth_date,
        tenant_email,
        tenant_mobile_phone,
        tenant_social_name,

        brokerage_comission_percent_amount,
        prolabore_percent_amount,
        pc_predial,
        pc_content,
        rent_value,
        insurance_prize,
        installment_number,

        lessor_name,
        lessor_document,
        lessor_email,

        address_zipcode,
        address_street,
        address_residence_type,
        address_number,
        address_complement,
        address_district,
        address_city,
        address_state,
        address_ibge,

        validity_start,
        monthly_validity,

        coverages,
    };
};
